import { ClickAwayListener } from "@mui/base";
import Fade from "@mui/material/Fade";
import { theme, useResponsive } from "@product/scmp-sdk";
import { useSyncedRef } from "@react-hookz/web";
import { useScroll } from "ahooks";
import { usePopupState } from "material-ui-popup-state/hooks";
import type { FunctionComponent, RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { centerAlignModifier } from "~/components/common/popper/modifiers";
import { query } from "~/components/header/header-oneline-menu-container";
import { useOnelineMenuTracking } from "~/components/header/header-oneline-menu-container/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useEditionValue } from "~/lib/edition";
import { useCurrentPageType } from "~/lib/router/hooks";
import type { headerAllMenuQuery$key } from "~/queries/__generated__/headerAllMenuQuery.graphql";
import type { headerOnelineMenuContainerQuery } from "~/queries/__generated__/headerOnelineMenuContainerQuery.graphql";

import {
  Container,
  HeaderAllMenuTopicsAndFocus,
  MegaMenuContainer,
  StyledHeaderAllMenuApplications,
  StyledHeaderAllMenuColumnists,
  StyledHeaderAllMenuFocus,
  StyledHeaderAllMenuHighlight,
  StyledHeaderAllMenuSection,
  StyledHeaderAllMenuTopics,
  StyledIconArrow,
  StyledPopper,
  Title,
} from "./styles";

export type Props = {
  className?: string;
  containerReference: RefObject<HTMLDivElement>;
  queryReference: PreloadedQuery<headerOnelineMenuContainerQuery>;
};

export const HeaderAllMenu: FunctionComponent<Props> = ({
  className,
  containerReference,
  queryReference,
}) => {
  const data = usePreloadedQuery(query, queryReference);
  const queries = useFragment<headerAllMenuQuery$key>(
    graphql`
      fragment headerAllMenuQuery on Query {
        ...headerAllMenuTopicsQuery
        ...headerAllMenuColumnistsQuery
        ...headerAllMenuFocusQuery
        ...headerAllMenuHighlightsQuery
        ...headerAllMenuSectionAppConfigQuery
      }
    `,
    data,
  );

  const modifiers = useMemo(() => [centerAlignModifier], []);

  const popupId = "tooltip-mega-menu";
  const popupState = usePopupState({ popupId, variant: "popper" });

  const isXLargeDesktopUp = useResponsive(theme.breakpoints.up("xLargeDesktop"));
  const currentContainerReference = useMemo(() => {
    if (isXLargeDesktopUp) return containerReference.current;
    return null;
  }, [containerReference, isXLargeDesktopUp]);

  const targetReference = useRef<HTMLDivElement>(null);
  const handleClose = useCallback(
    (event: Event | React.SyntheticEvent) => {
      // Prevent the menu getting close when clicking the target
      if (
        targetReference.current &&
        targetReference.current.contains(event.target as HTMLElement)
      ) {
        return;
      }

      popupState.setOpen(false);
    },
    [popupState],
  );

  const useHideMenuEffect = () => {
    // Hide the menu when user scroll
    const scroll = useScroll();
    const isOpenReference = useSyncedRef(popupState.isOpen);
    const setOpenReference = useSyncedRef(popupState.setOpen);
    useEffect(() => {
      if (!isOpenReference.current) return;
      setOpenReference.current(false);
    }, [isOpenReference, scroll, setOpenReference]);

    // Hide the menu when screen size smaller than desktop
    const isDesktopDown = useResponsive(theme.breakpoints.down("desktop"));
    useEffect(() => {
      if (isDesktopDown) setOpenReference.current(false);
    }, [isDesktopDown, setOpenReference]);
  };
  useHideMenuEffect();

  const currentPageType = useCurrentPageType();
  const edition = useEditionValue();
  const queryParameter = useMemo(
    () => ({
      module: `oneline_menu_all_${edition}`,
      pgtype: currentPageType,
    }),
    [currentPageType, edition],
  );

  const { edition: trackingEdition } = useOnelineMenuTracking();
  useEffect(() => {
    if (!popupState.isOpen) return;
    sendGATracking({
      action: "Linemenu/Click",
      category: "Menu",
      label: `${trackingEdition}/Linemenu/All/Open`,
    });
  }, [trackingEdition, popupState.isOpen]);

  if (!queryReference) return null;

  return (
    <StyledPopper
      bindType="toggle"
      container={currentContainerReference}
      keepMounted={true}
      modifiers={modifiers}
      placement="bottom"
      popupComponent={({ TransitionProps }) => (
        <ClickAwayListener onClickAway={handleClose}>
          <Fade {...TransitionProps} timeout={200}>
            <MegaMenuContainer>
              <StyledHeaderAllMenuSection queryParameter={queryParameter} reference={queries} />
              <HeaderAllMenuTopicsAndFocus>
                <StyledHeaderAllMenuTopics queryParameter={queryParameter} reference={queries} />
                <StyledHeaderAllMenuFocus queryParameter={queryParameter} reference={queries} />
              </HeaderAllMenuTopicsAndFocus>
              <StyledHeaderAllMenuApplications queryParameter={queryParameter} />
              <StyledHeaderAllMenuColumnists queryParameter={queryParameter} reference={queries} />
              <StyledHeaderAllMenuHighlight queryParameter={queryParameter} reference={queries} />
            </MegaMenuContainer>
          </Fade>
        </ClickAwayListener>
      )}
      popupId={popupId}
      popupState={popupState}
      transition={true}
      triggerComponent={
        <Container $isToggle={popupState.isOpen} className={className} ref={targetReference}>
          <Title>All</Title>
          <StyledIconArrow $isToggle={popupState.isOpen} />
        </Container>
      }
    />
  );
};

HeaderAllMenu.displayName = "HeaderAllMenu";
